import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project } from '../../types';

import { Button } from '../../components/Common/button';
import { Input } from '../../components/Common/input';
import { Textarea } from '../../components/Common/textarea';

const NewMissionForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<Project>>({
    name: '',
    description: '',
    client: '',
    scope: '',
    startDate: '',
    endDate: '',
    status: 'draft',
    team: [],
    companyInfo: {
      name: '',
      sector: '',
      size: '',
      revenue: '',
      location: '',
      mainContact: {
        name: '',
        role: '',
        email: '',
        phone: ''
      }
    },
    plan: []
  });

  type DepartmentLocal = { id: string; name: string; interviews: { business: number; technical: number } };
  const [departments, setDepartments] = useState<DepartmentLocal[]>([]);

  const handleAddDepartment = () => {
    const newDepartment: DepartmentLocal = {
      id: Date.now().toString(),
      name: '',
      interviews: {
        business: 0,
        technical: 0
      }
    };
    setDepartments([...departments, newDepartment]);
  };

  const handleDepartmentChange = (id: string, field: string, value: string | number) => {
    setDepartments(departments.map(dept => {
      if (dept.id === id) {
        if (field === 'name') {
          return { ...dept, name: value as string };
        } else if (field === 'business' || field === 'technical') {
          return {
            ...dept,
            interviews: {
              ...dept.interviews,
              [field]: Number(value)
            }
          };
        }
      }
      return dept;
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically make an API call to create the mission
    navigate('/missions');
  };

  return (
    <div className="max-w-4xl mx-auto py-6">
      <h1 className="text-2xl font-bold text-foreground mb-6">{t('missions.newMission')}</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-medium text-foreground mb-4">{t('missions.generalInfo')}</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.missionName')}
              </label>
              <Input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.client')}
              </label>
              <Input
                type="text"
                required
                value={formData.client}
                onChange={(e) => setFormData({...formData, client: e.target.value})}
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.description')}
              </label>
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.startDate')}
              </label>
              <Input
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.endDate')}
              </label>
              <Input
                type="date"
                required
                value={formData.endDate}
                onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-medium text-foreground mb-4">{t('missions.companyInfo')}</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.companyName')}
              </label>
              <Input
                type="text"
                required
                value={formData.companyInfo?.name}
                onChange={(e) => setFormData({
                  ...formData,
                  companyInfo: { ...formData.companyInfo!, name: e.target.value }
                })}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('missions.sector')}
              </label>
              <Input
                type="text"
                required
                value={formData.companyInfo?.sector}
                onChange={(e) => setFormData({
                  ...formData,
                  companyInfo: { ...formData.companyInfo!, sector: e.target.value }
                })}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Interview Plan */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground">{t('missions.interviewPlan')}</h2>
            <Button type="button" onClick={handleAddDepartment} variant="outline" className="inline-flex items-center">
              <Plus className="h-4 w-4 mr-1" />
              {t('missions.addDepartment')}
            </Button>
          </div>

          <div className="space-y-4">
            {departments.map((dept) => (
              <div key={dept.id} className="border-b border-border pb-4">
                <div className="flex items-center justify-between mb-2">
                  <Input
                    type="text"
                    placeholder={t('missions.departmentNamePlaceholder')}
                    value={dept.name}
                    onChange={(e) => handleDepartmentChange(dept.id, 'name', e.target.value)}
                    className="block w-full"
                  />
                  <Button
                    type="button"
                    onClick={() => setDepartments(departments.filter(d => d.id !== dept.id))}
                    variant="ghost"
                    size="icon"
                    className="ml-2 text-muted-foreground hover:text-red-500"
                    aria-label={t('missions.removeDepartment')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('missions.businessInterviews')}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={dept.interviews.business}
                      onChange={(e) => handleDepartmentChange(dept.id, 'business', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('missions.technicalInterviews')}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={dept.interviews.technical}
                      onChange={(e) => handleDepartmentChange(dept.id, 'technical', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <Button type="button" variant="outline" onClick={() => navigate('/missions')}>
            {t('global.cancel')}
          </Button>
          <Button type="submit">{t('missions.createMission')}</Button>
        </div>
      </form>
    </div>
  );
};

export default NewMissionForm;